/**
 * AUTH57 Slice C — Travel static shell Service Worker policy (source of truth).
 * Used by Jest and by scripts/write-travel-sw.js to emit build/travel-sw.js.
 *
 * SW caches ONLY same-origin static shell assets. Never Cache API for APIs,
 * Authorization, JWT, Journey/Today JSON, or Google Maps/Places.
 */

const TRAVEL_SW_CACHE_PREFIX = 'travel-static-v1';
const TRAVEL_SW_SCRIPT_URL = '/travel-sw.js';

/** Same-origin API / BFF prefixes on Travel nginx (must never enter SW Cache). */
const TRAVEL_API_PATH_PREFIXES = Object.freeze([
  '/travel/',
  '/formosa-member/',
  '/formosa-admin/',
  '/formosa-order/',
  '/formosa-payment/',
  '/formosa-market/',
  '/im/',
  '/api/',
  '/cdb/',
]);

/** Host suffixes that must never be cached as AUTH57 offline assets. */
const TRAVEL_GOOGLE_HOST_SUFFIXES = Object.freeze([
  'googleapis.com',
  'gstatic.com',
  'google.com',
  'googleusercontent.com',
  'ggpht.com',
  'g.co',
]);

function normalizeOrigin(origin) {
  return String(origin || '').replace(/\/$/, '');
}

function hostMatchesSuffix(hostname, suffix) {
  const host = String(hostname || '').toLowerCase();
  const s = String(suffix || '').toLowerCase();
  return host === s || host.endsWith(`.${s}`);
}

function isGoogleRemoteUrl(url) {
  try {
    const parsed = typeof url === 'string' ? new URL(url) : url;
    return TRAVEL_GOOGLE_HOST_SUFFIXES.some((suffix) => hostMatchesSuffix(parsed.hostname, suffix));
  } catch {
    return false;
  }
}

function isTravelApiPathname(pathname) {
  const path = String(pathname || '');
  return TRAVEL_API_PATH_PREFIXES.some((prefix) => path === prefix.slice(0, -1) || path.startsWith(prefix));
}

function isTravelApiUrl(url, selfOrigin) {
  try {
    const parsed = typeof url === 'string' ? new URL(url, selfOrigin) : url;
    const origin = normalizeOrigin(selfOrigin || parsed.origin);
    if (normalizeOrigin(parsed.origin) !== origin) {
      // Cross-origin API hosts (local CRA proxy target) — never cache.
      if (isTravelApiPathname(parsed.pathname)) return true;
      return false;
    }
    return isTravelApiPathname(parsed.pathname);
  } catch {
    return true;
  }
}

function isSameOrigin(url, selfOrigin) {
  try {
    const parsed = typeof url === 'string' ? new URL(url, selfOrigin) : url;
    return normalizeOrigin(parsed.origin) === normalizeOrigin(selfOrigin);
  } catch {
    return false;
  }
}

/**
 * Hashed CRA assets and shell documents eligible for static cache.
 * Excludes source maps (not required for offline boot).
 */
function isTravelStaticAssetUrl(url, selfOrigin) {
  try {
    const parsed = typeof url === 'string' ? new URL(url, selfOrigin) : url;
    if (!isSameOrigin(parsed, selfOrigin)) return false;
    if (isTravelApiPathname(parsed.pathname)) return false;
    const path = parsed.pathname;
    if (path === '/' || path === '/index.html') return true;
    if (path === TRAVEL_SW_SCRIPT_URL) return false;
    if (path.startsWith('/static/')) {
      return !path.endsWith('.map');
    }
    // Optional root assets if present (favicon etc.) — never HTML routes.
    if (/^\/[^/]+\.(js|css|ico|png|jpg|jpeg|svg|webp|woff2?)$/i.test(path)) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * @returns {'bypass'|'network-only'|'cache-first-static'|'navigation-shell'}
 */
function decideTravelSwFetchAction({
  url,
  method = 'GET',
  mode = 'cors',
  hasAuthorization = false,
  selfOrigin,
}) {
  const verb = String(method || 'GET').toUpperCase();
  if (verb !== 'GET' && verb !== 'HEAD') {
    return 'network-only';
  }
  if (hasAuthorization) {
    return 'network-only';
  }

  let parsed;
  try {
    parsed = new URL(String(url), selfOrigin);
  } catch {
    return 'bypass';
  }

  if (isGoogleRemoteUrl(parsed)) {
    return 'network-only';
  }

  if (!isSameOrigin(parsed, selfOrigin)) {
    return 'bypass';
  }

  if (isTravelApiUrl(parsed, selfOrigin)) {
    return 'network-only';
  }

  if (mode === 'navigate') {
    return 'navigation-shell';
  }

  if (isTravelStaticAssetUrl(parsed, selfOrigin)) {
    return 'cache-first-static';
  }

  return 'network-only';
}

function shouldPutInTravelStaticCache({
  url,
  method = 'GET',
  hasAuthorization = false,
  selfOrigin,
  responseOk = true,
  responseStatus = 200,
}) {
  if (!responseOk || responseStatus !== 200) return false;
  if (hasAuthorization) return false;
  const action = decideTravelSwFetchAction({
    url,
    method,
    mode: 'cors',
    hasAuthorization,
    selfOrigin,
  });
  return action === 'cache-first-static';
}

function isOwnedTravelStaticCacheName(cacheName) {
  return String(cacheName || '').startsWith(`${TRAVEL_SW_CACHE_PREFIX}-`);
}

function selectObsoleteTravelCaches(cacheNames, currentCacheName) {
  const current = String(currentCacheName || '');
  return (cacheNames || []).filter(
    (name) => isOwnedTravelStaticCacheName(name) && name !== current,
  );
}

function extractBuildFingerprint(mainJsPath) {
  const match = String(mainJsPath || '').match(/main\.([a-f0-9]+)\.js/i);
  return match ? match[1] : 'dev';
}

function buildTravelCacheName(fingerprint) {
  return `${TRAVEL_SW_CACHE_PREFIX}-${fingerprint || 'dev'}`;
}

/**
 * Emit the Service Worker classic script source for build/travel-sw.js.
 */
function buildTravelServiceWorkerSource({ cacheName, precacheUrls }) {
  const urls = Array.from(new Set((precacheUrls || []).map((u) => String(u))));
  const apiPrefixesJson = JSON.stringify(TRAVEL_API_PATH_PREFIXES);
  const googleSuffixesJson = JSON.stringify(TRAVEL_GOOGLE_HOST_SUFFIXES);
  const cacheNameJson = JSON.stringify(cacheName);
  const precacheJson = JSON.stringify(urls);
  const prefixJson = JSON.stringify(TRAVEL_SW_CACHE_PREFIX);

  return `/* AUTH57 Travel static shell SW — generated; do not edit by hand. */
/* eslint-disable */
'use strict';
var TRAVEL_SW_CACHE_PREFIX = ${prefixJson};
var CACHE_NAME = ${cacheNameJson};
var PRECACHE_URLS = ${precacheJson};
var TRAVEL_API_PATH_PREFIXES = ${apiPrefixesJson};
var TRAVEL_GOOGLE_HOST_SUFFIXES = ${googleSuffixesJson};

function hostMatchesSuffix(hostname, suffix) {
  var host = String(hostname || '').toLowerCase();
  var s = String(suffix || '').toLowerCase();
  return host === s || host.endsWith('.' + s);
}

function isGoogleRemoteUrl(url) {
  try {
    var parsed = new URL(url);
    for (var i = 0; i < TRAVEL_GOOGLE_HOST_SUFFIXES.length; i++) {
      if (hostMatchesSuffix(parsed.hostname, TRAVEL_GOOGLE_HOST_SUFFIXES[i])) return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

function isTravelApiPathname(pathname) {
  var path = String(pathname || '');
  for (var i = 0; i < TRAVEL_API_PATH_PREFIXES.length; i++) {
    var prefix = TRAVEL_API_PATH_PREFIXES[i];
    if (path === prefix.slice(0, -1) || path.startsWith(prefix)) return true;
  }
  return false;
}

function isSameOrigin(url) {
  try {
    return new URL(url).origin === self.location.origin;
  } catch (e) {
    return false;
  }
}

function isTravelStaticAssetUrl(url) {
  try {
    var parsed = new URL(url, self.location.origin);
    if (parsed.origin !== self.location.origin) return false;
    if (isTravelApiPathname(parsed.pathname)) return false;
    var path = parsed.pathname;
    if (path === '/' || path === '/index.html') return true;
    if (path === '/travel-sw.js') return false;
    if (path.indexOf('/static/') === 0) return path.slice(-4) !== '.map';
    return /^\\/[^/]+\\.(js|css|ico|png|jpg|jpeg|svg|webp|woff2?)$/i.test(path);
  } catch (e) {
    return false;
  }
}

function decideAction(request) {
  var method = String(request.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') return 'network-only';
  if (request.headers && request.headers.get('Authorization')) return 'network-only';
  var url = request.url;
  if (isGoogleRemoteUrl(url)) return 'network-only';
  if (!isSameOrigin(url)) return 'bypass';
  try {
    var parsed = new URL(url);
    if (isTravelApiPathname(parsed.pathname)) return 'network-only';
  } catch (e) {
    return 'bypass';
  }
  if (request.mode === 'navigate') return 'navigation-shell';
  if (isTravelStaticAssetUrl(url)) return 'cache-first-static';
  return 'network-only';
}

function shouldCacheResponse(request, response) {
  if (!response || !response.ok || response.status !== 200) return false;
  if (request.headers && request.headers.get('Authorization')) return false;
  return decideAction(request) === 'cache-first-static';
}

self.addEventListener('install', function (event) {
  event.waitUntil(
    caches.open(CACHE_NAME).then(function (cache) {
      return Promise.all(PRECACHE_URLS.map(function (url) {
        return fetch(new Request(url, { cache: 'reload' })).then(function (response) {
          if (response && response.ok) return cache.put(url, response);
        });
      }));
    }).then(function () {
      return self.skipWaiting();
    })
  );
});

self.addEventListener('activate', function (event) {
  event.waitUntil(
    caches.keys().then(function (keys) {
      return Promise.all(
        keys
          .filter(function (name) {
            return name.indexOf(TRAVEL_SW_CACHE_PREFIX + '-') === 0 && name !== CACHE_NAME;
          })
          .map(function (name) {
            return caches.delete(name);
          })
      );
    }).then(function () {
      return self.clients.claim();
    })
  );
});

self.addEventListener('fetch', function (event) {
  var request = event.request;
  var action = decideAction(request);

  if (action === 'bypass' || action === 'network-only') {
    return;
  }

  if (action === 'navigation-shell') {
    event.respondWith(
      fetch(request).then(function (response) {
        if (response && response.ok) {
          var copy = response.clone();
          caches.open(CACHE_NAME).then(function (cache) {
            cache.put('/index.html', copy);
          });
        }
        return response;
      }).catch(function () {
        return caches.open(CACHE_NAME).then(function (cache) {
          return cache.match('/index.html').then(function (hit) {
            if (hit) return hit;
            return cache.match('/').then(function (rootHit) {
              return rootHit || Response.error();
            });
          });
        });
      })
    );
    return;
  }

  if (action === 'cache-first-static') {
    event.respondWith(
      caches.open(CACHE_NAME).then(function (cache) {
        return cache.match(request).then(function (cached) {
          if (cached) return cached;
          return fetch(request).then(function (response) {
            if (shouldCacheResponse(request, response)) {
              cache.put(request, response.clone());
            }
            return response;
          });
        });
      })
    );
  }
});
`;
}

module.exports = {
  TRAVEL_SW_CACHE_PREFIX,
  TRAVEL_SW_SCRIPT_URL,
  TRAVEL_API_PATH_PREFIXES,
  TRAVEL_GOOGLE_HOST_SUFFIXES,
  isGoogleRemoteUrl,
  isTravelApiPathname,
  isTravelApiUrl,
  isTravelStaticAssetUrl,
  decideTravelSwFetchAction,
  shouldPutInTravelStaticCache,
  isOwnedTravelStaticCacheName,
  selectObsoleteTravelCaches,
  extractBuildFingerprint,
  buildTravelCacheName,
  buildTravelServiceWorkerSource,
};
